import { Autocomplete, Button, Input, Textarea } from "@mantine/core";
import {
  IconBan,
  IconBriefcase,
  IconBuilding,
  IconFileText,
  IconHash,
  IconId,
  IconMail,
  IconMapPin,
  IconPencil,
  IconPhone,
  IconPhoneCall,
  IconPrinter,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { apiUserGetMe } from "@/api";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

function CoopPage() {
  const { t } = useTranslation();
  const [coopData, setCoopData] = useState<any>(null);
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [editData, setEditData] = useState<any>({});
  const [pauseAccount, setPauseAccount] = useState(false);
  const [defaultApplicant, setDefaultApplicant] = useState<any>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    name = "",
    abbreviation = "",
    serialNumber = "",
    businessIdNumber = "",
    shippingAddress = "",
    invoiceType = "",
    businessAddress,
    telephone = "",
    fax = "",
    contacts = [],
    business = "",
    invoiceAddress,
    reviewer,
    reviewDate,
    applicationDate,
    status,
    disableReason,
  } = coopData || {};

  const isEditable = !!coopData && (!status || status === "normal");

  const handleSubmitEdit = async ({
    id,
    editData,
  }: {
    id: string;
    editData: any;
  }) => {
    try {
      await single(
        orpcClient.partner.requestDisable([
          {
            id: id!,
            disableReason: editData?.disableReason ?? "",
          },
        ])
      );

      navigate(`/hrMainPage/coop/${id}`);
      window.location.reload();
    } catch (error) {
      console.error("partner.requestDisable_ERROR", error);
    }
  };

  const handleChangeMembers = (value: string, employeeData: any[]) => {
    let match = value.match(/ID:\s*(\d+)/);
    let idNumber = match ? match[1] : null;

    let selectedData = employeeData.filter((e: any) => e?.id == idNumber)[0];

    handleChangeCoopData(selectedData, "applicant");
  };

  const handleChangeCoopData = (value: any, type: string) => {
    setCoopData((prevData: any) => ({
      ...prevData,
      [type]: value,
    }));

    setEditData((prevData: any) => ({
      ...prevData,
      [type]: value,
    }));
  };

  const handleDisplaySelectMembers = (data: any[]) => {
    return data?.map((t: any) => t?.name + ` (ID: ${t?.id})`);
  };

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex min-h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
        {value || value === 0 ? value : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  const renderControl = (
    label: string,
    control: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      {control}
    </div>
  );

  const renderAddressSection = (
    title: string,
    addr: any
  ) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <IconMapPin size={16} className="text-blue-600" />
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
        {renderField(t("coop.city") || "縣市", addr?.city, IconMapPin)}
        {renderField(t("coop.district") || "行政區", addr?.district, IconMapPin)}
        {renderField(t("coop.postalCode") || "郵遞區號", addr?.postalCode, IconHash)}
        {renderField(t("coop.address") || "地址", addr?.address, IconMapPin)}
      </div>
    </div>
  );

  const handleDisplayDeactivation = () => {
    if (!status || status === "normal") {
      return (
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <IconBan size={16} className="text-blue-600" />
              <h3 className="text-sm font-medium text-gray-700">停用申請</h3>
            </div>
            <Button
              variant="default"
              radius="md"
              size="sm"
              onClick={() => setPauseAccount((prev) => !prev)}
            >
              {pauseAccount
                ? t("hrMain.cancelApplication") || "取消申請"
                : t("hrMain.apply") || "立即申請"}
            </Button>
          </div>
          {pauseAccount ? (
            <div className="space-y-4 p-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {renderControl(
                  "申請人",
                  <Autocomplete
                    disabled={status === "underReview"}
                    placeholder="Input component"
                    defaultValue={
                      defaultApplicant?.name + ` (ID: ${defaultApplicant?.id})`
                    }
                    leftSection={<IconUser size={16} />}
                    data={handleDisplaySelectMembers(employeeData)}
                    onChange={(e) => handleChangeMembers(e, employeeData)}
                    onOptionSubmit={(e) => handleChangeMembers(e, employeeData)}
                  />,
                  IconUser
                )}
              </div>
              {renderControl(
                "停用原因",
                <Textarea
                  placeholder="Input placeholder"
                  value={disableReason}
                  disabled={status === "underReview"}
                  onChange={(e) =>
                    handleChangeCoopData(e.target.value, "disableReason")
                  }
                />,
                IconFileText
              )}
              <div className="flex gap-4">
                <Button
                  variant="default"
                  style={{ flex: 1 }}
                  radius="md"
                  onClick={() => setPauseAccount(false)}
                >
                  取消
                </Button>
                <Button
                  variant="filled"
                  color="gray"
                  style={{ flex: 1 }}
                  radius="md"
                  disabled={!pauseAccount}
                  onClick={() => handleSubmitEdit({ id: id!, editData })}
                >
                  {t("hrMain.confirmDeactivation") || "確定停用"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-400">
              此合作方目前為正常狀態。
            </div>
          )}
        </div>
      );
    } else if (status === "underReview") {
      return (
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBan size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">停用申請</h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {renderControl(
                "申請人",
                <Autocomplete
                  value={coopData?.applicant?.name}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconUser size={16} />}
                />,
                IconUser
              )}
              {renderControl(
                "審核人",
                <Input
                  value={reviewer?.name}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconUser size={16} />}
                />,
                IconUser
              )}
              {renderControl(
                "申請時間",
                <Input
                  value={applicationDate}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconFileText size={16} />}
                />,
                IconFileText
              )}
              {renderControl(
                "審核狀態",
                <Input
                  value={"審核中"}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconFileText size={16} />}
                />,
                IconFileText
              )}
            </div>
            {renderControl(
              "停用原因",
              <Textarea
                placeholder="Input placeholder"
                value={disableReason}
                disabled={true}
              />,
              IconFileText
            )}
            <div className="flex gap-4">
              <Button
                variant="default"
                style={{ flex: 1 }}
                radius="md"
                onClick={async () => {
                  await single(
                    orpcClient.partner.cancelDisableRequest([{ id: id! }])
                  );
                  window.location.reload();
                }}
              >
                {t("hrMain.cancelApplication") || "取消申請"}
              </Button>
              <Button
                variant="filled"
                color="gray"
                style={{ flex: 1 }}
                radius="md"
                onClick={async () => {
                  await single(
                    orpcClient.partner.approveDisable([{ id: id! }])
                  );
                  window.location.reload();
                }}
              >
                核准停用
              </Button>
            </div>
          </div>
        </div>
      );
    } else if (status === "disabled") {
      return (
        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBan size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">已停用</h3>
          </div>
          <div className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {renderControl(
                "申請人",
                <Autocomplete
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconUser size={16} />}
                  data={handleDisplaySelectMembers(employeeData)}
                />,
                IconUser
              )}
              {renderControl(
                "申請時間",
                <Input
                  value={applicationDate}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconFileText size={16} />}
                />,
                IconFileText
              )}
              {renderControl(
                "審核人",
                <Input
                  value={reviewer?.name}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconUser size={16} />}
                />,
                IconUser
              )}
              {renderControl(
                "審核時間",
                <Input
                  value={reviewDate}
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconFileText size={16} />}
                />,
                IconFileText
              )}
              {renderControl(
                "審核狀態",
                <Input
                  value="已停用"
                  disabled={true}
                  placeholder="Input component"
                  leftSection={<IconFileText size={16} />}
                />,
                IconFileText
              )}
            </div>
            {renderControl(
              "停用原因",
              <Textarea
                placeholder="Input placeholder"
                value={disableReason}
                disabled={true}
              />,
              IconFileText
            )}
          </div>
        </div>
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        let result = await single(orpcClient.partner.read([{ id: id! }]));
        setCoopData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let currentUserID = await apiUserGetMe();
        let result = await orpcClient.employee.list({});

        let userEmployee = result?.find((t) => t?.userId === currentUserID?.id);
        setDefaultApplicant(userEmployee);
        handleChangeCoopData(userEmployee, "applicant");
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
              {name || t("coop.viewTitle") || "合作方詳細資訊"}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("coop.viewTitle") || "合作方詳細資訊"}
            </div>
          </div>
          {isEditable ? (
            <button
              type="button"
              aria-label={t("coop.edit") || "編輯合作方"}
              onClick={() => navigate(`/hrMainPage/editCoop/${id}`)}
              className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
            >
              <IconPencil size={18} />
            </button>
          ) : null}
        </div>

        {handleDisplayDeactivation()}

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconFileText size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.basicInfo") || "基本資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(t("coop.companyName") || "公司名", name, IconBuilding)}
            {renderField(t("coop.abbreviation") || "簡稱", abbreviation, IconBuilding)}
            {renderField(
              t("coop.businessIdNumber") || "統編",
              businessIdNumber,
              IconId
            )}
            {renderField("廣達組織 ID", serialNumber, IconHash)}
          </div>
        </div>

        {renderAddressSection(
          t("coop.businessRegisterAddress") || "1. 營業登記地址",
          businessAddress
        )}
        {renderAddressSection(
          t("coop.invoiceAddressSection") || "2. 發票地址",
          invoiceAddress
        )}
        {renderAddressSection(
          t("coop.shippingAddressSection") || "3. 送貨地址",
          shippingAddress
        )}

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconPhoneCall size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.contactInfo") || "聯絡資訊"}
            </h3>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(t("coop.phone") || "電話", telephone, IconPhone)}
            {renderField(t("coop.fax") || "傳真", fax, IconPrinter)}
            {renderField(t("coop.invoiceType") || "發票種類", invoiceType, IconFileText)}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconUsers size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.contactPersonInfo") || "聯絡人資訊"}
            </h3>
          </div>
          <div className="space-y-4 p-6">
            {!contacts || contacts.length === 0 ? (
              <div className="text-sm text-gray-400">
                {t("coop.noContacts") || "尚未新增聯絡人"}
              </div>
            ) : (
              contacts.map((contact: any, index: number) => (
                <div key={index} className="space-y-3">
                  <div className="text-sm font-medium" style={{ color: "#6F6F6F" }}>
                    {t("coop.contactNumber", { number: index + 1 }) ||
                      `聯絡人 #${index + 1}`}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {renderField(
                      t("coop.contactPerson") || "聯絡人",
                      contact?.name,
                      IconUser
                    )}
                    {renderField(
                      t("coop.department") || "部門",
                      contact?.department,
                      IconBuilding
                    )}
                    {renderField(
                      t("coop.phone") || "電話",
                      contact?.telephone,
                      IconPhone
                    )}
                    {renderField(
                      t("coop.email") || "E-mail",
                      contact?.email,
                      IconMail
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
            <IconBriefcase size={16} className="text-blue-600" />
            <h3 className="text-sm font-medium text-gray-700">
              {t("coop.businessType") || "營業項目"}
            </h3>
          </div>
          <div className="p-6">
            {renderField(t("coop.businessType") || "營業項目", business, IconBriefcase)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CoopPage;
